import { Link, useFetcher, useSearchParams } from "@remix-run/react"
import { Check, LoaderCircle, XCircleIcon } from "lucide-react"
import { Button } from "~/components/ui/button"

import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect, useRef } from "react"
import { useState } from "react"
import { useForm } from "react-hook-form"
import toast from "react-hot-toast"
import { z } from "zod"
import { action, loader } from "~/.server/route-container/authentication/login"
import StepIndicator from "~/components/sign-up/StepIndicator"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "~/components/ui/form"
import { Input } from "~/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select"
import { Switch } from "~/components/ui/switch"
import { useSignup } from "~/hooks/SignupContext"

export { action, loader }

const alphabet = [
  "А",
  "Б",
  "В",
  "Г",
  "Д",
  "Е",
  "Ё",
  "Ж",
  "З",
  "И",
  "Й",
  "К",
  "Л",
  "М",
  "Н",
  "О",
  "Ө",
  "П",
  "Р",
  "С",
  "Т",
  "У",
  "Ү",
  "Ф",
  "Х",
  "Ц",
  "Ч",
  "Ш",
  "Щ",
  "Ъ",
  "Ы",
  "Ь",
  "Э",
  "Ю",
  "Я",
]

const cyrillicNameRegex = /^[А-ЯЁӨҮа-яёөү\s-]+$/
const sanitizeCyrillicName = (value: string) =>
  value.replace(/[^А-Яа-яЁёӨөҮү\s-]/g, "")

export default function SignUpInfoPage() {
  const { signupData, setSignupData } = useSignup()
  const [searchParams] = useSearchParams()
  const initialPromoCode = searchParams.get("code") ?? ""
  const [useInviteCode, setUseInviteCode] = useState(Boolean(initialPromoCode))
  const containerRef = useRef<HTMLDivElement>(null)
  const [availableHeight, setAvailableHeight] = useState("100dvh")
  const [promoCode, setPromoCode] = useState(initialPromoCode)
  type FetcherData = { message?: string; error?: string }
  const fetcher = useFetcher<FetcherData>()
  type PromoValidationData = { valid?: boolean; message?: string }
  const promoFetcher = useFetcher<PromoValidationData>()
  const [validatedPromoCode, setValidatedPromoCode] = useState("")
  const [promoValidationStatus, setPromoValidationStatus] = useState<
    "idle" | "valid" | "invalid"
  >(initialPromoCode ? "idle" : "idle")
  const [promoValidationMessage, setPromoValidationMessage] = useState("")
  const isLoading = fetcher.state !== "idle"

  const stepOneSchema = z.object({
    firstName: z
      .string({
        required_error: "Нэр оруулна уу",
        invalid_type_error: "Нэр оруулна уу",
      })
      .min(1, { message: "Нэр оруулна уу" })
      .regex(cyrillicNameRegex, {
        message: "Зөвхөн кирилл үсэг ашиглана уу",
      }),
    lastName: z
      .string({
        required_error: "Овог оруулна уу",
        invalid_type_error: "Овог оруулна уу",
      })
      .min(1, { message: "Овог оруулна уу" })
      .regex(cyrillicNameRegex, {
        message: "Зөвхөн кирилл үсэг ашиглана уу",
      }),
    phone: z
      .string({
        required_error: "Утасны дугаар оруулна уу",
        invalid_type_error: "Утасны дугаар оруулна уу",
      })
      .min(8, { message: "Утасны дугаар оруулна уу" }),
    email: z
      .string({
        required_error: "И-мэйл хаяг оруулна уу",
        invalid_type_error: "И-мэйл хаяг оруулна уу",
      })
      .email({ message: "И-мэйл хаяг оруулна уу" }),
    registryNumber: z.object({
      first: z
        .string({
          required_error: "Эхний үсэг",
          invalid_type_error: "Эхний үсэг",
        })
        .min(1, { message: "Эхний үсэг" }),
      second: z
        .string({
          required_error: "Хоёр дахь үсэг",
          invalid_type_error: "Хоёр дахь үсэг",
        })
        .min(1, { message: "Хоёр дахь үсэг" }),
      number: z
        .string({
          required_error: "Дугаар оруулна уу",
          invalid_type_error: "Дугаар оруулна уу",
        })
        .min(8, { message: "Дугаар оруулна уу" })
        .max(8, { message: "Зөв оруулна уу" })
        .regex(/^\d+$/, { message: "Зөв оруулна уу" }),
    }),
  })
  const registryValue =
    typeof signupData.registryNumber === "object" && signupData.registryNumber
      ? signupData.registryNumber
      : null

  const normalizedRegistryNumber = {
    first: registryValue?.first ?? "",
    second: registryValue?.second ?? "",
    number: registryValue?.number ?? "",
  }

  const form = useForm<z.infer<typeof stepOneSchema>>({
    resolver: zodResolver(stepOneSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      ...signupData,
      registryNumber: normalizedRegistryNumber,
    },
  })

  const handleValidatePromoCode = (value?: string) => {
    const codeToValidate = (value ?? promoCode).trim()
    if (!codeToValidate) {
      setPromoValidationStatus("invalid")
      setPromoValidationMessage("Урилгын код оруулна уу")
      return
    }

    const formData = new FormData()
    formData.append("code", codeToValidate)
    promoFetcher.submit(formData, {
      method: "POST",
      action: "/sign-up/validate-invitation-code",
    })
  }

  function onSubmit(values: z.infer<typeof stepOneSchema>) {
    const trimmedPromoCode = promoCode.trim()
    if (useInviteCode) {
      if (!trimmedPromoCode) {
        toast.error("Урилгын код оруулна уу")
        return
      }

      if (
        promoValidationStatus !== "valid" ||
        validatedPromoCode !== trimmedPromoCode
      ) {
        toast.error(promoValidationMessage || "Урилгын кодыг шалгана уу")
        return
      }
    }

    const formData = new FormData()

    // Append all fields except registryNumber
    for (const [key, value] of Object.entries(values)) {
      if (key === "registryNumber") continue // skip for now
      formData.append(key, value as string)
    }

    // Combine registryNumber parts into one string and append
    const registryNumberString =
      values.registryNumber.first +
      values.registryNumber.second +
      values.registryNumber.number

    if (useInviteCode && trimmedPromoCode) {
      formData.append("promoCode", trimmedPromoCode)
    }
    formData.append("registryNumber", registryNumberString)
    setSignupData({
      ...values,
      registryNumber: registryNumberString,
      promoCode: useInviteCode ? trimmedPromoCode : "",
    })

    fetcher.submit(formData, {
      method: "POST",
      action: "/sign-up/send-code",
    })
  }

  useEffect(() => {
    if (fetcher.data) {
      if (fetcher.data.message) {
        toast.success(fetcher.data.message)
      } else if (fetcher.data?.error) {
        toast.error(fetcher.data?.error)
      }
    }
  }, [fetcher.data])

  useEffect(() => {
    const response = promoFetcher.data
    if (!response) return

    if (response.valid) {
      setPromoValidationStatus("valid")
      setValidatedPromoCode(promoCode.trim())
      setPromoValidationMessage(response.message ?? "Код зөв байна")
      return
    }

    setPromoValidationStatus("invalid")
    setValidatedPromoCode("")
    setPromoValidationMessage(response.message ?? "Код хүчингүй байна")
  }, [promoFetcher.data, promoCode])

  useEffect(() => {
    if (!useInviteCode) {
      setPromoValidationStatus("idle")
      setValidatedPromoCode("")
      setPromoValidationMessage("")
      return
    }

    if (promoCode.trim() !== validatedPromoCode) {
      setPromoValidationStatus("idle")
      setPromoValidationMessage("")
    }
  }, [promoCode, useInviteCode, validatedPromoCode])

  useEffect(() => {
    if (initialPromoCode) {
      handleValidatePromoCode(initialPromoCode)
    }
  }, [])

  useEffect(() => {
    const updateAvailableHeight = () => {
      const topOffset = containerRef.current?.getBoundingClientRect().top ?? 0
      setAvailableHeight(`calc(100dvh - ${Math.max(topOffset, 0)}px)`)
    }

    updateAvailableHeight()
    window.addEventListener("resize", updateAvailableHeight)
    return () => window.removeEventListener("resize", updateAvailableHeight)
  }, [])

  return (
    <div
      ref={containerRef}
      className="flex items-center justify-center sm:px-4 px-2"
      style={{ minHeight: availableHeight }}
    >
      <div className="w-full max-w-md mx-auto">
        <div className="text-sm text-muted-foreground">
          <div className="mb-12">
            <div className="text-3xl text-[#171717] font-extrabold mb-3 text-center">
              Бүртгүүлэх
            </div>
            <div className="text-center text-sm text-[#545454] mb-3">
              Хүний хэрэгцээ цөм багтсан
            </div>
          </div>
        </div>
        <div>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
              <fieldset
                disabled={fetcher.state !== "idle"}
                className="space-y-4"
              >
                <div className="grid grid-cols-2 gap-2">
                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            {...field}
                            id="lastName"
                            name="lastName"
                            placeholder="Овог"
                            className="rounded-xl border-[#E9E9E9] h-12"
                            onChange={(e) => field.onChange(e.target.value)}
                            onBlur={() => {
                              field.onBlur()
                              form.trigger("lastName")
                            }}
                          />
                        </FormControl>
                        {fieldState.error && (
                          <FormMessage className="text-red-500">
                            {fieldState.error.message}
                          </FormMessage>
                        )}
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            {...field}
                            id="firstName"
                            name="firstName"
                            placeholder="Нэр"
                            className="rounded-xl border-[#E9E9E9] h-12"
                            onChange={(e) => field.onChange(e.target.value)}
                            onBlur={() => {
                              field.onBlur()
                              form.trigger("firstName")
                            }}
                          />
                        </FormControl>
                        <FormMessage className="text-red-500" />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          {...field}
                          id="email"
                          name="email"
                          placeholder="И-мэйл хаяг"
                          className="rounded-xl border-[#E9E9E9] h-12"
                        />
                      </FormControl>
                      <FormMessage className="text-red-500" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          {...field}
                          id="phone"
                          name="phone"
                          placeholder="Утасны дугаар"
                          className="rounded-xl border-[#E9E9E9] h-12"
                        />
                      </FormControl>
                      <FormMessage className="text-red-500" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="registryNumber"
                  render={({ field }) => {
                    const value = field.value ?? {
                      first: "",
                      second: "",
                      number: "",
                    }
                    const errors = form.formState.errors.registryNumber as
                      | {
                          first?: { message?: string }
                          second?: { message?: string }
                          number?: { message?: string }
                        }
                      | undefined

                    return (
                      <FormItem>
                        <FormControl>
                          <div className="flex gap-2">
                            {/* First letter */}
                            <div className="space-y-1">
                              <Select
                                value={
                                  typeof value !== "string" ? value.first : ""
                                }
                                onValueChange={(val) =>
                                  typeof value !== "string"
                                    ? field.onChange({ ...value, first: val })
                                    : field.onChange(value)
                                }
                              >
                                <SelectTrigger className="w-12 rounded-xl text-gray-500 border-[#E9E9E9] h-12 [&>svg]:hidden flex justify-center">
                                  <SelectValue placeholder="А" />
                                </SelectTrigger>
                                <SelectContent>
                                  <div className="grid grid-cols-5 gap-1 p-1">
                                    {alphabet.map((letter) => (
                                      <SelectItem key={letter} value={letter}>
                                        {letter}
                                      </SelectItem>
                                    ))}
                                  </div>
                                </SelectContent>
                              </Select>
                              {errors?.first?.message && (
                                <p className="text-red-500 text-xs">
                                  {errors.first.message}
                                </p>
                              )}
                            </div>

                            {/* Second letter */}
                            <div className="space-y-1">
                              <Select
                                value={
                                  typeof value !== "string" ? value.second : ""
                                }
                                onValueChange={(val) =>
                                  typeof value !== "string"
                                    ? field.onChange({
                                        ...value,
                                        second: val,
                                      })
                                    : field.onChange(value)
                                }
                              >
                                <SelectTrigger className="w-12 rounded-xl text-gray-500 border-[#E9E9E9] h-12 [&>svg]:hidden flex justify-center">
                                  <SelectValue placeholder="А" />
                                </SelectTrigger>
                                <SelectContent>
                                  <div className="grid grid-cols-5 gap-1 p-1">
                                    {alphabet.map((letter) => (
                                      <SelectItem key={letter} value={letter}>
                                        {letter}
                                      </SelectItem>
                                    ))}
                                  </div>
                                </SelectContent>
                              </Select>
                              {errors?.second?.message && (
                                <p className="text-red-500 text-xs">
                                  {errors.second.message}
                                </p>
                              )}
                            </div>

                            {/* Number */}
                            <div className="flex-1 space-y-1">
                              <Input
                                type="text"
                                placeholder="Регистрийн дугаар"
                                value={
                                  typeof value === "object" ? value.number : ""
                                }
                                onChange={(e) =>
                                  field.onChange({
                                    ...(typeof value === "object" ? value : {}),
                                    number: e.target.value,
                                  })
                                }
                                className="rounded-xl border-[#E9E9E9] h-12"
                              />
                              {errors?.number?.message && (
                                <p className="text-red-500 text-xs">
                                  {errors.number.message}
                                </p>
                              )}
                            </div>
                          </div>
                        </FormControl>
                      </FormItem>
                    )
                  }}
                />

                <div className="space-y-2 bg-[#F3F5F6] p-4 rounded-xl">
                  <div className="flex items-center justify-between text-sm">
                    Урилгын код оруулах
                    <Switch
                      id="invite-code-switch"
                      checked={useInviteCode}
                      onCheckedChange={setUseInviteCode}
                    />
                  </div>

                  {useInviteCode && (
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Input
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value)}
                          placeholder="Урилгын код"
                          className="rounded-xl border-[#E9E9E9] h-12"
                        />
                        <Button
                          type="button"
                          className="px-2 rounded-xl border-[#E9E9E9]"
                          onClick={() => handleValidatePromoCode()}
                          disabled={promoFetcher.state !== "idle"}
                        >
                          {promoFetcher.state !== "idle" ? (
                            <LoaderCircle className="h-4 w-4 animate-spin" />
                          ) : (
                            <span>Шалгах</span>
                          )}
                        </Button>
                      </div>
                      {promoValidationStatus === "invalid" && (
                        <p className="text-xs text-red-500">
                          {promoValidationMessage}
                        </p>
                      )}
                      {promoValidationStatus === "valid" && (
                        <p className="text-xs text-green-600">
                          {promoValidationMessage || "Код зөв байна"}
                        </p>
                      )}
                    </div>
                  )}
                </div>
                {fetcher.data?.error && (
                  <div className="rounded-md bg-red-50 p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <XCircleIcon
                          className="h-5 w-5 text-red-400"
                          aria-hidden="true"
                        />
                      </div>
                      <div className="ml-3">
                        <p className="text-red-500 text-sm">
                          {fetcher.data?.error}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="mt-5">
                  <Button
                    id="lgn_btn"
                    type="submit"
                    className="w-full justify-center text-white hover:opacity-95 transition-opacity bg-primary rounded-xl h-12"
                  >
                    <span className="flex gap-4 items-center">
                      {isLoading && (
                        <LoaderCircle className="h-5 w-5 animate-spin" />
                      )}
                      Үргэлжлүүлэх
                    </span>
                  </Button>
                </div>
                <div className="pt-1">
                  <Link
                    to="/sign-in"
                    className="block text-center text-sm text-primary hover:text-indigo-500"
                  >
                    Нэвтрэх - рүү буцах
                  </Link>
                </div>
              </fieldset>
            </form>
          </Form>
        </div>
      </div>
    </div>
  )
}
